import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './RecordingProvider.css';

/**
 * App-level screen-recording session. Lives ABOVE the route tree so a recording
 * survives navigating between pages (essential for capturing repro steps). Any
 * page can start a recording via useRecording().start(uploader); the floating
 * Stop control, the review modal, and the upload all live here at the app root.
 */
const RecordingContext = createContext(null);
export const useRecording = () => useContext(RecordingContext);

const isSupported = typeof navigator !== 'undefined'
  && navigator.mediaDevices
  && typeof navigator.mediaDevices.getDisplayMedia === 'function'
  && typeof window !== 'undefined'
  && typeof window.MediaRecorder !== 'undefined';

function pickMimeType() {
  const candidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  const MR = window.MediaRecorder;
  for (const t of candidates) {
    if (typeof MR.isTypeSupported === 'function' && MR.isTypeSupported(t)) return t;
  }
  return 'video/webm';
}

function mapError(err) {
  const name = err?.name || '';
  const msg = err?.message || '';
  if (name === 'NotAllowedError') {
    return /system/i.test(msg)
      ? 'Your browser is blocked from recording the screen. Grant it Screen Recording permission (macOS: System Settings ▸ Privacy & Security ▸ Screen Recording), then fully quit and reopen the browser.'
      : 'Screen sharing was cancelled or blocked.';
  }
  if (name === 'NotFoundError') return 'No screen/window was available to record.';
  if (name === 'NotSupportedError') return 'Screen recording isn’t supported in this browser.';
  return msg || 'Could not start recording.';
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export function RecordingProvider({ children }) {
  const [active, setActive] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null); // { file, url }
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamsRef = useRef([]);
  const timerRef = useRef(null);
  const mimeRef = useRef('video/webm');
  const uploaderRef = useRef(null);
  const returnToRef = useRef(null);
  const navigate = useNavigate();

  const cleanupStreams = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    streamsRef.current.forEach((s) => { try { s.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ } });
    streamsRef.current = [];
  };

  const start = useCallback(async (uploader) => {
    if (active || pending) return;
    setError(null);
    uploaderRef.current = uploader;
    // Remember the page the recording was started from, to return there after attaching.
    returnToRef.current = window.location.pathname + window.location.search;
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: false });
      let micStream = null;
      try { micStream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch (e) { /* video only */ }
      streamsRef.current = [displayStream, micStream].filter(Boolean);
      const tracks = [...displayStream.getVideoTracks()];
      if (micStream) tracks.push(...micStream.getAudioTracks());
      const combined = new MediaStream(tracks);

      const mime = pickMimeType();
      mimeRef.current = mime;
      const recorder = new window.MediaRecorder(combined, { mimeType: mime });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        cleanupStreams();
        setActive(false);
        const type = mimeRef.current.split(';')[0];
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        if (blob.size > 0) {
          const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const file = new File([blob], `screen-recording-${stamp}.webm`, { type });
          setPending({ file, url: URL.createObjectURL(blob) });
        } else {
          setError('The recording came through empty — please try again.');
        }
      };
      recorder.onerror = (ev) => { setError('Recording error: ' + (ev?.error?.message || 'unknown')); };
      const vt = displayStream.getVideoTracks()[0];
      if (vt) vt.addEventListener('ended', () => {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
      });

      recorder.start(1000);
      recorderRef.current = recorder;
      setActive(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      cleanupStreams();
      setActive(false);
      setError(mapError(err));
    }
  }, [active, pending]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
  }, []);

  const discard = () => {
    setPending((p) => { if (p?.url) { try { URL.revokeObjectURL(p.url); } catch (e) { /* noop */ } } return null; });
  };

  const attach = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (uploaderRef.current) await uploaderRef.current(pending.file);
      try { window.dispatchEvent(new CustomEvent('support-case:attachment-added')); } catch (e) { /* noop */ }
      discard();
      // Return to the case the recording was started from.
      const back = returnToRef.current;
      returnToRef.current = null;
      if (back) { try { navigate(back); } catch (e) { /* noop */ } }
      setToast('Recording attached to the case.');
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      setError('Failed to attach recording: ' + (err.response?.data?.error || err.message || 'unknown'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => () => { cleanupStreams(); }, []);

  const value = { supported: isSupported, active, start, stop };

  return (
    <RecordingContext.Provider value={value}>
      {children}

      {active && (
        <div className="recording-pill" role="status">
          <span className="recording-pill-dot" aria-hidden />
          Recording · {fmt(elapsed)}
          <button type="button" className="recording-pill-stop" onClick={stop}>Stop</button>
        </div>
      )}

      {toast && <div className="recording-toast">{toast}</div>}
      {error && !active && !pending && (
        <div className="recording-toast recording-toast-error" onClick={() => setError(null)}>{error}</div>
      )}

      {pending && (
        <div className="recording-modal" role="dialog" aria-modal="true">
          <div className="recording-modal-box">
            <h3 className="recording-modal-title">Review your recording</h3>
            <video src={pending.url} controls autoPlay className="recording-preview" />
            {error && <div className="recording-modal-error">{error}</div>}
            <div className="recording-modal-actions">
              <button type="button" className="recording-discard-btn" onClick={discard} disabled={busy}>Discard</button>
              <button type="button" className="recording-attach-btn" onClick={attach} disabled={busy}>
                {busy ? 'Attaching…' : 'Attach recording'}
              </button>
            </div>
          </div>
        </div>
      )}
    </RecordingContext.Provider>
  );
}

export default RecordingProvider;
